import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import {
  changelogCategoriesQueryOptions,
  useCreateChangelogCategory,
  useUpdateChangelogCategory,
  useDeleteChangelogCategory,
} from "@/hooks/use-changelog";
import { CategoryBadge } from "./category-badge";
import type { ChangelogCategory } from "@/types/changelog";

const categorySchema = z.object({
  nameKo: z.string().min(1, "한국어 이름을 입력해주세요"),
  nameEn: z.string().min(1, "영어 이름을 입력해주세요"),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "올바른 HEX 색상을 입력해주세요"),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

interface CategoryManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CategoryManagementDialog({
  open,
  onOpenChange,
}: CategoryManagementDialogProps) {
  const { data: categories } = useSuspenseQuery(
    changelogCategoriesQueryOptions(),
  );
  const createMutation = useCreateChangelogCategory();
  const updateMutation = useUpdateChangelogCategory();
  const deleteMutation = useDeleteChangelogCategory();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: { nameKo: "", nameEn: "", color: "#22C55E" },
  });

  const startEdit = (category: ChangelogCategory) => {
    setEditingId(category._id);
    setIsAdding(false);
    form.reset({
      nameKo: category.name.ko,
      nameEn: category.name.en ?? "",
      color: category.color,
    });
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    form.reset({ nameKo: "", nameEn: "", color: "#22C55E" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    form.reset();
  };

  const onSubmit = async (values: CategoryFormValues) => {
    const nameInput = { ko: values.nameKo, en: values.nameEn };

    try {
      if (editingId) {
        await updateMutation.mutateAsync({
          categoryId: editingId,
          name: nameInput,
          color: values.color,
        });
      } else {
        await createMutation.mutateAsync({
          name: nameInput,
          color: values.color,
          sortOrder: categories.length,
        });
      }
      cancelEdit();
    } catch {
      // 에러는 mutation onError에서 toast로 처리됨
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!confirm("이 카테고리를 삭제하시겠습니까?")) return;
    try {
      await deleteMutation.mutateAsync(categoryId);
    } catch {
      // 에러는 mutation onError에서 toast로 처리됨
    }
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>카테고리 관리</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {categories.map((category) => (
            <div key={category._id}>
              {editingId === category._id ? (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-2 rounded-lg border p-3"
                  >
                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name="nameKo"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>한국어</FormLabel>
                            <FormControl>
                              <Input placeholder="신규" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="nameEn"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel>English</FormLabel>
                            <FormControl>
                              <Input placeholder="New" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="color"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>색상</FormLabel>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={field.value}
                              onChange={field.onChange}
                              className="h-8 w-8 cursor-pointer rounded border"
                            />
                            <FormControl>
                              <Input
                                placeholder="#22C55E"
                                className="flex-1"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cancelEdit}
                        disabled={isPending}
                      >
                        <X className="mr-1 h-3 w-3" />
                        취소
                      </Button>
                      <Button type="submit" size="sm" disabled={isPending}>
                        <Check className="mr-1 h-3 w-3" />
                        {isPending ? "저장 중..." : "저장"}
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : (
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <CategoryBadge
                    name={category.name.ko}
                    color={category.color}
                  />
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(category)}
                      disabled={isPending}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(category._id)}
                      disabled={isPending}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {isAdding ? (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-2 rounded-lg border border-dashed p-3"
              >
                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="nameKo"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>한국어</FormLabel>
                        <FormControl>
                          <Input placeholder="신규" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="nameEn"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>English</FormLabel>
                        <FormControl>
                          <Input placeholder="New" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>색상</FormLabel>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="h-8 w-8 cursor-pointer rounded border"
                        />
                        <FormControl>
                          <Input
                            placeholder="#22C55E"
                            className="flex-1"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={cancelEdit}
                    disabled={isPending}
                  >
                    <X className="mr-1 h-3 w-3" />
                    취소
                  </Button>
                  <Button type="submit" size="sm" disabled={isPending}>
                    <Check className="mr-1 h-3 w-3" />
                    {isPending ? "추가 중..." : "추가"}
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <Button
              variant="outline"
              className="w-full border-dashed"
              onClick={startAdd}
              disabled={isPending}
            >
              <Plus className="mr-1 h-4 w-4" />
              카테고리 추가
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
